import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { apiErrorMessage } from "../API";
import type { RootState } from "../store";
import { deleteCall, getCallMedia, getCallPage, listPastCalls, prepareCallPage } from "./api";
import { CallListView } from "./CallViews";
import {
  LIST_POLL_MS,
  MEDIA_POLL_MS,
  isFreshCall,
  mayStillBeProcessing,
  mergeFirstPage,
  recordingReady,
} from "./processing";
import { downloadCall } from "./downloadCall";
import type { CallAttendee, CallListItem } from "./types";

const ATTENDEE_FETCHES = 4;
const SETTLED_KEY = "dooglie.calls.settled";

function loadSettled(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SETTLED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id) => typeof id === "string"));
  } catch {
    return new Set();
  }
}

function saveSettled(ids: ReadonlySet<string>) {
  try {
    sessionStorage.setItem(SETTLED_KEY, JSON.stringify([...ids].slice(-200)));
  } catch {
    // A private store still leaves this visit correct.
  }
}

export default function CallsListPage() {
  const canDelete = useSelector(
    (s: RootState) => s.user.is_dooglie_staff || s.user.is_org_admin,
  );
  const [calls, setCalls] = useState<CallListItem[]>([]);
  const [settled, setSettled] = useState<ReadonlySet<string>>(loadSettled);
  const [waiting, setWaiting] = useState<ReadonlySet<string>>(() => new Set());
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cursorRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  const seenCursors = useRef(new Set<string>());
  cursorRef.current = cursor;

  const load = useCallback((next: string | null) => {
    if (loadingRef.current) return;
    if (next && seenCursors.current.has(next)) {
      setCursor(null);
      return;
    }
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    listPastCalls(next)
      .then((page) => {
        if (next) seenCursors.current.add(next);
        setCalls((current) => (next ? [...current, ...page.calls] : page.calls));
        setCursor(page.cursor);
      })
      .catch((reason: unknown) => setError(apiErrorMessage(reason, "Could not load calls.")))
      .finally(() => {
        loadingRef.current = false;
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load(null);
  }, [load]);

  const callsRef = useRef(calls);
  callsRef.current = calls;
  const settledRef = useRef(settled);
  settledRef.current = settled;
  const waitingRef = useRef(waiting);
  waitingRef.current = waiting;
  const mediaDone = useRef(new Set<string>());
  const writeupDone = useRef(new Set<string>());
  const writeupChecked = useRef(new Set<string>());
  const prepareStarted = useRef(new Set<string>());

  // A meeting that just ended is a processing row on the same paint as it
  // joins the list. One this visit already finished stays a normal link.
  const pendingIds = useMemo(() => {
    const ids = new Set(waiting);
    for (const call of calls) {
      if (settled.has(call.bot_id)) continue;
      if (isFreshCall(call)) ids.add(call.bot_id);
    }
    return ids;
  }, [calls, settled, waiting]);

  const candidateKey = calls
    .filter((call) => mayStillBeProcessing(call) && !settled.has(call.bot_id))
    .map((call) => call.bot_id)
    .sort()
    .join("\n");

  useEffect(() => {
    if (!candidateKey) return;
    let cancelled = false;
    const openIds = () => candidateKey.split("\n").filter((id) => !settledRef.current.has(id));

    const markWaiting = (botId: string) => {
      if (cancelled || settledRef.current.has(botId) || waitingRef.current.has(botId)) return;
      const next = new Set(waitingRef.current);
      next.add(botId);
      waitingRef.current = next;
      setWaiting(next);
    };

    const finish = (botId: string) => {
      if (cancelled || !mediaDone.current.has(botId) || !writeupDone.current.has(botId)) return;
      if (!settledRef.current.has(botId)) {
        const next = new Set(settledRef.current);
        next.add(botId);
        settledRef.current = next;
        setSettled(next);
        saveSettled(next);
      }
      if (!waitingRef.current.has(botId)) return;
      const still = new Set(waitingRef.current);
      still.delete(botId);
      waitingRef.current = still;
      setWaiting(still);
    };

    const ensureWriteup = async (botId: string): Promise<"ready" | "missing" | "unknown"> => {
      if (writeupDone.current.has(botId)) return "ready";
      if (!writeupChecked.current.has(botId)) {
        writeupChecked.current.add(botId);
        try {
          const page = await getCallPage(botId);
          if (page.writeup_ready) {
            writeupDone.current.add(botId);
            return "ready";
          }
        } catch {
          writeupChecked.current.delete(botId);
          return "unknown";
        }
      }
      if (writeupDone.current.has(botId)) return "ready";
      if (prepareStarted.current.has(botId)) {
        // The write-up is a card the server computes after the first open:
        // look again, it lands a few seconds later.
        try {
          if ((await getCallPage(botId)).writeup_ready) {
            writeupDone.current.add(botId);
            return "ready";
          }
        } catch {
          return "unknown";
        }
        return "missing";
      }
      prepareStarted.current.add(botId);
      try {
        const prepared = await prepareCallPage(botId);
        if (prepared.writeup_ready) {
          writeupDone.current.add(botId);
          return "ready";
        }
        return "missing";
      } catch {
        prepareStarted.current.delete(botId);
        return "unknown";
      }
    };

    const check = async () => {
      await Promise.all(
        openIds().map(async (botId) => {
          const call = callsRef.current.find((row) => row.bot_id === botId);
          if (!call) return;
          let fileReady = false;
          try {
            const media = await getCallMedia(botId);
            fileReady = recordingReady(call, media);
            if (fileReady) mediaDone.current.add(botId);
          } catch {
            // A failed check is not evidence the meeting is still rendering.
            return;
          }
          if (!fileReady) markWaiting(botId);
          const writeup = await ensureWriteup(botId);
          if (writeup === "missing") markWaiting(botId);
          if (fileReady && writeup === "ready") finish(botId);
        }),
      );
    };

    void check();
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void check();
    }, MEDIA_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [candidateKey]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      if (loadingRef.current) return;
      listPastCalls(null)
        .then((page) => {
          setCalls((current) => mergeFirstPage(current, page.calls));
          setError(null);
        })
        .catch(() => undefined);
    };
    const timer = window.setInterval(refresh, LIST_POLL_MS);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  const onLoadMore = useCallback(() => {
    const next = cursorRef.current;
    if (next) load(next);
  }, [load]);

  const startedAttendees = useRef(new Set<string>());
  const attendeeQueue = useRef<string[]>([]);
  const attendeeFetches = useRef(0);

  const applyAttendees = useCallback((botId: string, attendees: CallAttendee[]) => {
    setCalls((current) =>
      current.map((row) => (row.bot_id === botId ? { ...row, attendees } : row)),
    );
  }, []);

  const fetchAttendees = useCallback(() => {
    while (attendeeFetches.current < ATTENDEE_FETCHES && attendeeQueue.current.length > 0) {
      const botId = attendeeQueue.current.shift();
      if (!botId) return;
      attendeeFetches.current += 1;
      getCallPage(botId)
        .then((page) => applyAttendees(botId, page.attendees ?? []))
        .catch(() => applyAttendees(botId, []))
        .finally(() => {
          attendeeFetches.current -= 1;
          fetchAttendees();
        });
    }
  }, [applyAttendees]);

  // The list route does not carry the cast yet. A row that already has
  // `attendees` (even empty) came from a list that does, and is left alone.
  useEffect(() => {
    let queued = false;
    for (const call of calls) {
      if (call.attendees != null || startedAttendees.current.has(call.bot_id)) continue;
      startedAttendees.current.add(call.bot_id);
      attendeeQueue.current.push(call.bot_id);
      queued = true;
    }
    if (queued) fetchAttendees();
  }, [calls, fetchAttendees]);

  return (
    <CallListView
      calls={calls}
      loading={loading}
      error={error}
      hasMore={cursor != null}
      onLoadMore={onLoadMore}
      pendingIds={pendingIds}
      canDelete={canDelete}
      onDelete={async (botId) => {
        try {
          await deleteCall(botId);
        } catch (reason: unknown) {
          throw new Error(apiErrorMessage(reason, "Could not delete this call."));
        }
        setCalls((current) => current.filter((call) => call.bot_id !== botId));
      }}
      onSave={(call, onStarted) => downloadCall(call.bot_id, onStarted)}
    />
  );
}
